import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, LogOut, UserCircle, X, KeyRound } from 'lucide-react'
import Logo from './Logo/Logo'
import {
  Boat as MyBookingsIcon,
  UserStar as FjordClubIcon,
  Coupon,
  CustomerService
} from '@fjordline/styles-v3'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion'
import FacebookIcon from './icons/FacebookIcon'
import InstagramIcon from './icons/InstagramIcon'
import YouTubeIcon from './icons/YouTubeIcon'
import AgentLoginIcon from './icons/AgentLoginIcon'
import { FlagSquareNorway, FlagSquareDenmark, FlagSquareGermany, FlagSquareUK } from './icons/FlagNorwayIcon'
import { Navigation_v2, Footer_v2, SubMenuItem_v2, NavigationItem_v2 } from './types'
import translations from './translations'
import { CheckCookies, signOutWithCookies } from './hooks/CheckCookies'
import { Button } from './ui/button'
import EcoFriendlyTravelIcon from './icons/EcoFriendlyTravelIcon'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router-dom'
import { navLinks } from '../../routes/navlinks'
import { routerRootPaths } from '../../routes/appRoutes/routerPaths'
import { FjordClubMemberStatus } from '../../types/MyPage'
import { useMyPage, useMyPageOperations } from '../../providers/myPageStateProvider/context'
import { BookingListNavLink } from '../../routes/navLinkFunctions'
import { Status } from '../../types/myPage/enums'
import { SHOW_PAY_WITH_CLUB_POINTS_PAGE } from '../../config'
import { useKeycloak } from '../../providers/KeycloakProvider'

const i18n = {
  defaultLocale: 'nb',
  locales: ['en', 'de', 'nb', 'da'],
} as const

// export type LocaleUrls = {
//   [key in Locale]: string
// }

export type Locale = (typeof i18n)['locales'][number]

const languageNamesWithIcons = {
  da: { name: 'DK', icon: FlagSquareDenmark },
  en: { name: 'EN', icon: FlagSquareUK },
  de: { name: 'DE', icon: FlagSquareGermany },
  nb: { name: 'NO', icon: FlagSquareNorway },
}

const MenuIcon = ({ itemId }: { itemId?: string }) => {
  const MenuIconWrapper = ({ children }: { children: React.ReactNode }) => {
    return <span className="mr-2">{children}</span>
  }

  switch (itemId) {
    case 'fjord-club':
      return <MenuIconWrapper><FjordClubIcon width={20} height={20} /></MenuIconWrapper>
    case 'my-travels':
      return <MenuIconWrapper><MyBookingsIcon width={24} height={24} /></MenuIconWrapper>
    case 'fjord-club-travels':
      return <MenuIconWrapper><Coupon width={20} height={20} /></MenuIconWrapper>
    case 'support':
      return <MenuIconWrapper><CustomerService width={20} height={20} /></MenuIconWrapper>
    default:
      return null
  }
}

const getMenuTitle = (itemId?: string, locale?: string) => {
  const { t } = useTranslation()
  switch (itemId) {
    case 'fjord-club':
      return t('navbar.fjordClub')
    case 'my-travels':
      return t('navbar.bookings')
    case 'fjord-club-travels':
      return t('payWithFjordClubPoints.navBar')
    case 'support':
      return t('navbar.support')
    case 'my-page':
      return translations.navigation.loggedIn.myAccount[locale ?? i18n.defaultLocale]
    default:
      return ''
  }
}

const MenuItem = (
  { item,
    fjordClubMemberStatus,
    isMobile = false,
    setIsOpen = () => {},
    setUserMenuOpen = () => {},
    setSubMenuItems = () => {},
    changesNotSaved = false,
    setShowChangesNotSavedModal = () => {}
  }
  :
  { 
    item: NavigationItem_v2,
    fjordClubMemberStatus: FjordClubMemberStatus,
    isMobile?: boolean,
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
    setUserMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>
    setSubMenuItems?: React.Dispatch<React.SetStateAction<SubMenuItem_v2[]>>
    changesNotSaved?: boolean
    setShowChangesNotSavedModal?: (value: boolean) => void
  }) => {
  const bookingListNavLink = useMemo(() => BookingListNavLink({ bookingListView: Status.FUTURE }), [])
  
  let link = item.href || ''
  let key = ''
  let showMenuItem = true

  switch (item.id) {
    case 'fjord-club':
      link = changesNotSaved
        ? navLinks.EDIT_MEMBERSHIP
        : fjordClubMemberStatus === FjordClubMemberStatus.userHasNoProfile
          ? navLinks.SIGN_UP
          : navLinks.FJORD_CLUB
      key = routerRootPaths.FJORD_CLUB
      break;
    case 'my-travels':
      link = changesNotSaved ? navLinks.EDIT_MEMBERSHIP : bookingListNavLink
      key = routerRootPaths.BOOKING_LIST
      break;
    case 'fjord-club-travels':
      showMenuItem = SHOW_PAY_WITH_CLUB_POINTS_PAGE
      link = navLinks.PayWithPoints
      key = routerRootPaths.PAY_WITH_CLUB_POINTS
      break;
    case 'support':
      link = navLinks.SUPPORT
      key = routerRootPaths.SUPPORT
      break;
    default:
      break;
  }

  if (!showMenuItem) return null

  if (!isMobile) {
    return (
      <NavLink
        onClick={() => {
          if (changesNotSaved && setShowChangesNotSavedModal) {
            setShowChangesNotSavedModal(true)
          }
        }}
        to={link}
        key={key}
        className="flex items-center hover:bg-gray-100 p-2 hover:rounded-lg"
        onMouseEnter={() => {
          setSubMenuItems([])
          setUserMenuOpen(false)
        }}
        data-testid={item.title}
      >
        <MenuIcon itemId={item.id} />{' '}
        <span className="hidden sm:flex ">{getMenuTitle(item.id)}</span>
      </NavLink>
    )
  }

  return (
    <Link className="text-2xl! font-playfair! text-[#373843] " to={link} key={key} onClick={() => setIsOpen(false)}>
      {getMenuTitle(item.id)}
    </Link>
  )
}

type NavigationProps = {
  navigationMenu: Navigation_v2
  children: React.ReactNode
  locale: Locale
  // setLocale: React.Dispatch<React.SetStateAction<Locale>>
  // localeUrls: Record<Locale, string>
  email?: string
  status: 'authenticated' | 'unauthenticated' | 'loading'
  signIn: (provider: string, options?: Record<string, unknown>, locale?: Record<string, unknown>) => void
  signOut: () => Promise<void>
  updatePassword: () => Promise<void>
  footerMenu: Footer_v2
  baseUrl: string
  isDevelopment: boolean
  excludeLanguages?: Locale[]
  // MIGRATION_SERVICE_URL: string
  // AUTH_REGISTRATION_URL: string
}

function NavBar({
  navigationMenu,
  children,
  locale,
  // setLocale,
  // localeUrls,
  email,
  // signIn,
  signOut,
  updatePassword,
  status,
  footerMenu,
  baseUrl,
  isDevelopment,
  excludeLanguages = [],
  // MIGRATION_SERVICE_URL,
  // AUTH_REGISTRATION_URL,
}: NavigationProps) {
  // CheckCookies({ status, signIn, signOut, isDevelopment })
  const [isOpen, setIsOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const { fjordClubMemberStatus } = useMyPage()

  const { i18n } = useTranslation()

  const handleLanguageChange = (lang: string) => {
    if (lang === locale) return
    // setLocale(() => lang as Locale)
    i18n.changeLanguage(lang)
    // window.location.href = localeUrls?.[lang as Locale]
  }

  const isAuthenticated = status === 'authenticated'
  const [subMenuItems, setSubMenuItems] = useState<SubMenuItem_v2[]>([])

  const [isVisible, setIsVisible] = useState(true) // Navbar visibility state
  const lastScrollY = useRef(0) // To keep track of the last scroll position

  const { changesNotSaved, setShowChangesNotSavedModal, setFjordClubMemberStatusNoProfile } = useMyPageOperations()
  const { isLoading: kc_isLoading } = useKeycloak()
  const isLoading = kc_isLoading
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated && fjordClubMemberStatus === FjordClubMemberStatus.userIsNotAuthenticated) {
      setFjordClubMemberStatusNoProfile()
    }
  }, [fjordClubMemberStatus, isAuthenticated, isLoading, setFjordClubMemberStatusNoProfile])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY === 0) {
        // Always show navbar when at the top of the page
        setIsVisible(true)
      } else if (currentScrollY > lastScrollY.current) {
        // User is scrolling down
        setIsVisible(false)
      } else {
        // User is scrolling up
        setIsVisible(true)
      }

      // Update the last scroll position
      lastScrollY.current = currentScrollY
    }

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll)

    // Call handler once to set the initial state correctly
    handleScroll()

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])
  return (
    <div className="min-h-screen flex flex-col">
      <nav
        id="navigation"
        className={`
          w-full bg-white
          ${isOpen ? 'relative' : 'sticky top-0'}
          ${isVisible ? 'translate-y-0' : '-translate-y-full'}
          transition-transform duration-300 ease-in-out
          z-50
        `}
      >
        <div className="h-[100px] flex items-center max-w-[1184px] m-auto relative flex-row-reverse lg:flex-row">
          <a
            id="fl-logo"
            className="pr-4 lg:pr-0 lg:-translate-x-[7px]  lg:mr-[12px]  ml-1 mr-1 "
            href={`${baseUrl}/${locale}`}
            data-testid="fl-logo"
          >
            <span className="xl:flex hidden lg:w-[150px]  ">
              <Logo width={150} height={32} />
            </span>
            <span className="xl:hidden w-[110px] ">
              <Logo width={110} height={24} />
            </span>
          </a>

          <div
            id="hamburger-menu"
            onClick={() => {
              setIsOpen(!isOpen)
              window.scrollTo(0, 0)
              setSubMenuItems([])
              setLangOpen(false)
              setUserMenuOpen(false)
            }}
            className={`cursor-pointer gap-1 focus:outline-hidden p-2 ml-4 lg:ml-0   mr-4  hover:bg-gray-100 rounded-lg items-center absolute lg:relative left-0  lg:hidden ${
              isOpen ? ' bg-gray-100 rounded-lg' : ''
            }`}
            data-testid="hamburger-menu"
          >
            <div className="w-[17px] h-[11px] relative scale-[1.2] lg:scale-[0.95] ">
              <div
                className={`absolute left-0 w-full h-[2px] bg-gray-800 transition-all duration-300 ease-out ${
                  isOpen ? 'top-[4.5px] rotate-45' : 'top-0'
                }`}
              />
              <div
                className={`absolute top-[4px] left-0 w-full h-[2px] bg-gray-800 transition-all duration-300 ease-out ${
                  isOpen ? 'opacity-0' : ''
                }`}
              />
              <div
                className={`absolute left-0 w-full h-[2px] bg-gray-800 transition-all duration-300 ease-out ${
                  isOpen ? 'top-[4.5px] -rotate-45' : 'top-[8px]'
                }`}
              />
            </div>
            <div onClick={() => setIsOpen(!isOpen)} className="ml-1 ">
              <span className="hidden lg:flex">{translations.navigation.menu[locale]}</span>
            </div>
          </div>
          <div className="flex w-full">
            <ul data-testid="ul-desktop" className="lg:flex list-none p-0 hidden w-full justify-between ">
              {navigationMenu?.items?.map((item, index) => {
                if (!item.primary) return null
                if (isAuthenticated && item.subMenu?.some((e) => e.action === 'login')) return null

                // TODO: Find correct route
                const route = Object.values(navLinks).find((e) => e === item.href)
                if (item.id === 'my-page' || item.id === 'fjord-club-travels' && !SHOW_PAY_WITH_CLUB_POINTS_PAGE) return null
                
                return (
                  <li
                    key={index}
                    className={`relative mt-auto mb-auto cursor-pointer transition-all ease-in-out duration-300 group`}
                    onClick={() => {
                      if (item.subMenu) {
                        setSubMenuItems(item.subMenu)
                        setIsOpen(false)
                      }
                    }}
                    >
                    <MenuItem 
                      item={item} 
                      fjordClubMemberStatus={fjordClubMemberStatus}
                      setUserMenuOpen={setUserMenuOpen}
                      setSubMenuItems={setSubMenuItems}
                      changesNotSaved={changesNotSaved} 
                      setShowChangesNotSavedModal={setShowChangesNotSavedModal} 
                    />

                    {/* {item.subMenu === subMenuItems && (
                      <div className="absolute left-0 top-full mt-2 bg-white shadow-lg z-10  min-w-full rounded-b-xl">
                        {subMenuItems.map((subItem, subIndex) => {
                          const isLastItem = subIndex === subMenuItems.length - 1
                          // if (!isAuthenticated && subItem.action === 'login') {
                          //   return (
                          //     <LoginPopover
                          //       MIGRATION_SERVICE_URL={MIGRATION_SERVICE_URL}
                          //       AUTH_REGISTRATION_URL={AUTH_REGISTRATION_URL}
                          //       signIn={signIn}
                          //       locale={locale}
                          //       title={subItem.title || ''}
                          //       key={subIndex}
                          //     />
                          //   )
                          // }
                          return (
                            // TODO: Change to NavLinks
                            <a className="" href={subItem.href} key={subIndex}>
                              {' '}
                              <div
                                key={subIndex}
                                className={`p-2 hover:bg-gray-100 ${isLastItem ? 'rounded-b-xl' : ''} `}
                              >
                                {subItem.title}
                              </div>
                            </a>
                          )
                        })}
                      </div>
                    )} */}
                  </li>
                )
              })}
              <AuthenticatedUserMenu
                setUserMenuOpen={setUserMenuOpen}
                email={email}
                userMenuOpen={userMenuOpen}
                locale={locale}
                // signOutWithCookies={() => signOutWithCookies(isDevelopment, signOut)}
                signOutWithCookies={signOut}
                updatePassword={updatePassword}
              />
              <LangSelect
                setLangOpen={setLangOpen}
                setUserMenuOpen={setUserMenuOpen}
                setIsOpen={setIsOpen}
                langOpen={langOpen}
                locale={locale}
                handleLanguageChange={handleLanguageChange}
                excludeLanguages={excludeLanguages}
              />
            </ul>
          </div>
          <div
            className={`absolute top-full p-0 ${
              isOpen
                ? 'lg:border lg:border-b lg:p-4 justify-center lg:justify-start flex lg:flex-row flex-col h-auto opacity-100 z-50 bg-white lg:text-left text-center rounded-b-xl w-full'
                : 'max-h-0 opacity-0'
            } bg-gray-200 lg:bg-white overflow-hidden transition-all  duration-500 flex flex-wrap lg:-ml-0 -ml-4 `}
          >
            <>
              {navigationMenu?.items?.map((menu, index) => {
                const isLastItem = navigationMenu?.items && index === navigationMenu?.items?.length - 1
                const navMenuWithoutShowDesktop = navigationMenu.items?.filter((e) => !e.primary)

                if (menu.primary === true) {
                  return (
                    <div
                      key={index}
                      className="lg:hidden border-b border-gray-100 bg-white pb-4 pt-4 flex items-center text-center justify-center w-full text-2xl!"
                    >
                      {menu.subMenu ? (
                        <Accordion type="single" collapsible>
                          <AccordionItem value={menu.title || ''}>
                            <AccordionTrigger className="justify-center font-playfair!">
                              {' '}
                              {getMenuTitle(menu.id ?? '', locale)}{' '}
                            </AccordionTrigger>
                            <AccordionContent className="mt-4">
                              {isAuthenticated && menu.id === 'my-page' ? (
                                <h2 key={index} className="gap-4 flex flex-col text-[24px] font-playfair!">
                                  <p
                                    className="text-base! font-normal!"
                                    data-testid="my-account-a-to-my-page"
                                    onClick={updatePassword}
                                  >
                                    {translations.navigation.loggedIn.updatePassword[locale]}
                                  </p>
                                  <p
                                    className="text-base! font-normal!"
                                    data-testid="my-account-logout"
                                    onClick={() => signOutWithCookies(isDevelopment, signOut)}
                                  >
                                    {translations.navigation.loggedIn.logout[locale]}
                                  </p>
                                </h2>
                              ) : null}
                              {/* {menu.subMenu?.map((e) => {
                                if (isAuthenticated && menu.id === 'my-page') return null
                                // if (e.action === 'login') {
                                //   return (
                                //     <LoginPopover
                                //       MIGRATION_SERVICE_URL={MIGRATION_SERVICE_URL}
                                //       AUTH_REGISTRATION_URL={AUTH_REGISTRATION_URL}
                                //       signIn={signIn}
                                //       locale={locale}
                                //       title={e.title || ''}
                                //       key={menu.id}
                                //     />
                                //   )
                                // }
                                return (
                                  <h2 key={index} className="text-2xl! font-medium! font-playfair!">
                                    <a key={index} href={e.href}>
                                      <p className="pb-4 text-base! font-normal! ">{e.title}</p>
                                    </a>
                                  </h2>
                                )
                              })} */}
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      ) : (
                        <h2 key={index} className="text-2xl! font-normal! font-playfair! ">
                          <MenuItem 
                            item={menu}
                            fjordClubMemberStatus={fjordClubMemberStatus} 
                            isMobile 
                            setIsOpen={setIsOpen}
                          />
                        </h2>
                      )}
                    </div>
                  )
                }

                return (
                  <Fragment key={menu._key}>
                    {menu._key === navMenuWithoutShowDesktop?.[0]._key && (
                      <div
                        className="lg:hidden flex flex-col  border-t border-gray-100 bg-gray-100"
                        key={menu._key + index}
                      >
                        <div
                          className="p-2 flex justify-center items-center cursor-pointer hover:bg-gray-100"
                          onClick={() => setLangOpen(!langOpen)}
                        >
                          <span className="items-center  flex gap-2">
                            {React.createElement(languageNamesWithIcons[locale].icon)}{' '}
                            {locale === 'nb' ? 'NO' : locale?.toUpperCase()}
                            <ChevronDown
                              className={`${
                                langOpen ? 'rotate-180' : ''
                              } h-4 w-4 shrink-0 transition-transform duration-200 `}
                            />
                          </span>
                          <span className="ml-2"></span>
                        </div>

                        {langOpen && (
                          <div
                            data-testid="lang-select-mobile"
                            className={`duration-200 overflow-hidden flex flex-col m-auto pr-10 border-b border-gray-100 transition-all ${
                              langOpen ? 'animate-accordion-down' : 'animate-accordion-up"'
                            }  `}
                          >
                            {Object.keys(languageNamesWithIcons).map((lang) => {
                              if (lang === locale) return null
                              const Icon = languageNamesWithIcons[lang]?.icon
                              return (
                                <div
                                  role="button"
                                  data-testid={`lang-select-${lang}`}
                                  key={lang}
                                  className="flex items-center gap-2 p-4 hover:bg-gray-100 cursor-pointer "
                                  onClick={() => handleLanguageChange(lang)}
                                >
                                  <Icon size={20} />

                                  <span>{languageNamesWithIcons[lang].name}</span>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    <div
                      data-testid="dropdown-menu-item"
                      className={`${menu.primary ? 'xl:hidden flex' : 'flex'} flex-col  ${
                        isLastItem ? '' : ' border-r'
                      } lg:bg-white bg-gray-100 lg:ml-4 border-gray-100 lg:pr-8`}
                      key={index}
                    >
                      <>
                        <div className="lg:ml-2 lg:mb-4 mt-4 mb-4 font-bold! text-sm! ">
                          <a
                            className="font-bold! text-sm!"
                            onClick={() => setIsOpen(false)}
                            data-testid={`dropdown-${menu.title}`}
                            href={menu.href || ''}
                          >
                            {menu.title}
                          </a>
                        </div>
                        {menu.subMenu?.map((subItem, subIndex) => {
                          return (
                            <a
                              className="text-sm! font-normal! "
                              key={subIndex}
                              onClick={() => setIsOpen(false)}
                              data-testid={`dropdown-${subItem.title}`}
                              href={subItem.href || ''}
                            >
                              <p
                                key={subIndex}
                                className="p-2 cursor-pointer transition-colors duration-100 hover:bg-GRAY_LIGHT hover:rounded-lg"
                              >
                                {subItem.title}
                              </p>
                            </a>
                          )
                        })}
                      </>
                    </div>
                  </Fragment>
                )
              })}
            </>
          </div>
        </div>
      </nav>
      <main className="flex-1 flex-col">{children}</main>
      <Footer footerMenu={footerMenu?.items || []} locale={locale} />
    </div>
  )
}
export default NavBar

const Footer = ({ footerMenu, email, locale }: { footerMenu: NavigationItem_v2[]; email?: string; locale }) => {
  return (
    <div id="footer" className="w-full bg-[#F7F7F8] h-auto p-4 sm:p-6 lg:p-10">
      <div className="flex m-auto justify-between max-w-[1184px] flex-wrap flex-col md:flex-col lg:flex-row text-center lg:text-left gap-6 lg:gap-4">
        {footerMenu.map((e, index) => {
          const hasNoSubMenus = e.subMenu?.length === 0
          if (e.action === 'login' && email) return null
          return (
            <div
              key={index}
              className={`
                flex-1 flex flex-col items-center lg:items-start px-4 sm:px-6 lg:px-8
                ${index !== footerMenu.length - 1 && e.id !== 'agent-login' ? 'border-b lg:border-0' : ''}
                ${index !== footerMenu.length - 1 && e.id !== 'agent-login' ? 'lg:border-r' : ''}
                ${e.id === 'agent-login' ? 'order-first md:order-first lg:order-none border-b lg:border-b-0' : ''}
                border-[#D7D8E0] pb-6 pt-6
              `}
            >
              <a
                href={e.href || ''}
                data-testid={`footer-nav-${e.title}`}
                className={`lg:text-nowrap ${hasNoSubMenus ? 'hover:underline' : ''} mb-4`}
              >
                <span className={`flex gap-2 items-center font-bold`}>
                  {e.id === 'agent-login' ? <AgentLoginIcon /> : null}
                  {e.title}
                </span>
              </a>
              {e.id === 'agent-login' && <EcoFriendlyTravelIcon />}
              {hasNoSubMenus ? null : (
                <div className="flex flex-col items-center lg:items-start gap-4 mt-2 lg:mt-4 w-full">
                  {e.subMenu?.map((sub, subIndex) => {
                    const isSocialMedia =
                      sub.title === 'Facebook' || sub.title === 'Instagram' || sub.title === 'YouTube'

                    if (isSocialMedia && subIndex === 0) {
                      // Create a separate row for social media icons
                      const socialIcons = e.subMenu?.filter((item) =>
                        ['Facebook', 'Instagram', 'YouTube'].includes(item.title || ''),
                      )
                      return (
                        <div key={subIndex} className="flex flex-row gap-4 justify-center lg:justify-start w-full">
                          {socialIcons?.map((socialSub, socialIndex) => (
                            <a
                              key={socialIndex}
                              href={socialSub.href || ''}
                              className="hover:underline cursor-pointer  flex items-center"
                              data-testid={`footer-nav-${socialSub.title}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <span className="flex items-center">
                                {socialSub.title === 'Facebook' ? (
                                  <FacebookIcon />
                                ) : socialSub.title === 'Instagram' ? (
                                  <InstagramIcon />
                                ) : (
                                  <YouTubeIcon />
                                )}
                              </span>
                            </a>
                          ))}
                        </div>
                      )
                    }

                    return !isSocialMedia ? (
                      <a
                        href={sub.href || ''}
                        className="hover:underline cursor-pointer flex items-center text-nowrap w-full justify-center lg:justify-start"
                        data-testid={`footer-nav-${sub.title}`}
                        key={subIndex}
                      >
                        <span className="flex items-center">{sub.title}</span>
                      </a>
                    ) : null
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div
        data-testid="footer-privacy-and-cookies"
        className="max-w-[1184px] w-full gap-4 mt-6 lg:mt-[100px] m-auto flex justify-evenly  px-4 sm:px-6 lg:px-8 flex-col lg:flex-row text-center lg:text-left"
      >
        <div className="text-nowrap">© {new Date().getFullYear()} Fjord Line AS.</div>
        <PrivacyInformationText locale={locale} />
      </div>
    </div>
  )
}

function PrivacyInformationText({ locale }: { locale: Locale }) {
  if (locale === 'nb') {
    return (
      <div>
        Vi respekterer og beskytter personvernet og personopplysningene dine.{' '}
        <a href="https://fjordline.com/nb/p/informasjonskapsler" className="underline">
          Les om bruk av informasjonskapsler
        </a>
        {' '}(cookies), og om hvordan Fjord Line samler inn og bruker{' '}
        <a href="https://fjordline.com/nb/p/personvern" className="underline">
          personopplysninger
        </a>
        .
      </div>
    )
  }

  if (locale === 'da') {
    return (
      <div>
        Vi respekterer og beskytter dit privatliv og dine personoplysninger. Læs om{' '}
        <a href="https://fjordline.com/da/p/cookiedeklaration" className="underline">
          brug af cookies
        </a>
        , og om hvordan Fjord Line indsamler og bruger{' '}
        <a href="https://fjordline.com/da/p/privatlivspolitik" className="underline">
          personoplysninger
        </a>
        .
      </div>
    )
  }

  if (locale === 'de') {
    return (
      <div>
        Wir respektieren und schützen Ihre Privatsphäre und Ihre persönlichen Daten. Informieren Sie sich über die{' '}
        <a href="https://fjordline.com/de/p/cookie-erklaerung" className="underline">
          Nutzung von Cookies
        </a>
        {' '}und die Art und Weise, wie Fjord Line{' '}
        <a href="https://fjordline.com/de/p/datenschutz" className="underline">
          persönliche Daten
        </a>
        {' '}sammelt und verwendet.
      </div>
    )
  }

  if (locale === 'en') {
    return (
      <div>
        We respect and protect your privacy and personal information. Read about the{' '}
        <a href="https://fjordline.com/en/p/cookiedeclaration" className="underline">
          use of cookies
        </a>{' '}
        and how Fjord Line collects and uses{' '}
        <a href="https://fjordline.com/en/p/privacy" className="underline">
          personal information
        </a>
        .
      </div>
    )
  } else {
    return null
  }
}

type LangSelectProps = {
  setLangOpen: React.Dispatch<React.SetStateAction<boolean>>
  setUserMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  langOpen: boolean
  locale: Locale
  excludeLanguages?: Locale[]
  handleLanguageChange: (lang: string) => void
}

const LangSelect = ({
  setLangOpen,
  setUserMenuOpen,
  setIsOpen,
  langOpen,
  locale,
  handleLanguageChange,
  excludeLanguages = [],
}: LangSelectProps) => {
  return (
    <div data-testid="lang-select" className={`hidden lg:flex hover:bg-gray-100 rounded-lg p-2  relative`}>
      <div
        onClick={() => {
          setLangOpen(!langOpen)
          setUserMenuOpen(false)
          setIsOpen(false)
        }}
        className="cursor-pointer flex items-center transition ease-in-out duration-300"
      >
        <span className=" items-center flex gap-2">
          <div className="block ">{locale === 'nb' ? 'NO' : locale === 'da' ? 'DK' : locale?.toUpperCase()}</div>
          <div>{React.createElement(languageNamesWithIcons[locale]?.icon)} </div>
        </span>
        {/* <span className="ml-2"></span> */}
      </div>

      {langOpen && (
        <div
          className="absolute top-full right-0 w-auto p-1 bg-white shadow-lg rounded-md z-40  transition ease-in-out duration-300 rounded-b-xl"
          onMouseLeave={() => setLangOpen(false)}
          onBlur={() => setLangOpen(false)}
        >
          {Object.keys(languageNamesWithIcons)?.map((lang) => {
            if (lang === locale) return null
            if (excludeLanguages.includes(lang as Locale)) return null
            const Icon = languageNamesWithIcons[lang]?.icon
            return (
              <div
                key={lang}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer text-center w-auto justify-center rounded-b-lg "
                onClick={() => handleLanguageChange(lang)}
              >
                <span>{languageNamesWithIcons[lang].name}</span>
                <Icon size={20} /> {/* Language flag */}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

type AuthenticatedUserMenuProps = {
  setUserMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
  userMenuOpen: boolean
  locale: Locale
  email?: string
  signOutWithCookies: () => void
  updatePassword: () => void
}

const AuthenticatedUserMenu = ({
  setUserMenuOpen,
  userMenuOpen,
  locale,
  email,
  signOutWithCookies,
  updatePassword,
}: AuthenticatedUserMenuProps) => {
  if (!email) return null

  return (
    <div>
      <div
        data-testid="my-account"
        className={`relative p-2 hidden lg:flex items-center rounded-lg hover:bg-gray-100  cursor-pointer ${
          userMenuOpen && 'bg-gray-100'
        }`}
        onClick={() => {
          setUserMenuOpen(!userMenuOpen)
        }}
      >
        <span className="block xl:block">
          <UserCircle className="sm:w-[24px] sm:h-[24px] w-[24px] h-[24px] " />
        </span>
        <span className="sm:block w-full hidden text-center min-w-[80px]  ">
          {translations.navigation.loggedIn.myAccount[locale]}
        </span>
      </div>
      {userMenuOpen && (
        <div
          className="absolute top-full  w-auto  -mt-6 flex flex-wrap min-w-[250px] max-w-[350px] p-4 bg-white shadow-lg rounded-xl z-40 transition ease-in-out duration-300 border"
          onBlur={() => setUserMenuOpen(false)}
          onMouseLeave={() => setUserMenuOpen(false)}
        >
          <Button
            onClick={() => {
              setUserMenuOpen(false)
            }}
            className="right-0 absolute top-0 p-2 mr-2 bg-gray-50 rounded-full mt-1"
          >
            <X />
          </Button>
          <div className="p-2 ">
            <div className="mb-2 ">{translations.navigation.loggedIn.myAccount[locale]}</div>

            <div data-testid={`my-account-username-${email}`} className=" mb-2 break-all ">
              {translations.navigation.loggedIn.loggedInAs[locale]} <br />
              {email}
            </div>
            <hr />
            <div
              data-testid="my-account-a-to-change-password"
              onClick={updatePassword}
              className="flex gap-2 mt-4 border-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer max-w-[200px]"
            >
              <KeyRound /> <span>{translations.navigation.loggedIn.updatePassword[locale]}</span>
            </div>
            <div
              data-testid="my-account-logout"
              onClick={signOutWithCookies}
              className="flex gap-2 mt-4 border-2 p-2 rounded-lg hover:bg-gray-100 cursor-pointer max-w-[200px]"
            >
              <LogOut /> {translations.navigation.loggedIn.logout[locale]}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
